import {noop} from '@benchling/js-utils';
import React from 'react';

import {CreateAnalysisParams} from 'analysis-tool/AnalysisDAO';
import {CreateAnalysisModal} from 'analysis-tool/create-analysis-modal/CreateAnalysisModal';
import {SeedDataConfigType} from 'analysis-tool/create-analysis-modal/SeedDataTypes';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {ProcedureType, StudyType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  isFeatureEnabledForProcedureType,
  ProceduresFeature,
} from 'procedures/utils/ProcedureCapabilityManagementHelpers';
import {
  CREATE_ANALYSIS_NO_PERMISSION_MESSAGE,
  getInitialAnalysisNameForStudy,
  NO_PROCEDURE_RUNS_TO_ANALYZE_MESSAGE,
} from 'studies/typed/utils/analysis/analysisUtils';
import {createAnalysisForStudy} from 'studies/typed/utils/analysis/createAnalysisForStudy';
import ProcedureBasedStudyAnalysisModalBanner from 'studies/typed/utils/analysis/ProcedureBasedStudyAnalysisModalBanner';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import legacyRunModal from 'ui-library/legacyRunModal';

const STUDY_ARCHIVED_MESSAGE = 'Unarchive your study to generate an analysis';

/**
 * Props for AnalyzeMultipleProcedureRunsButton component.
 *
 * @package exported for testing.
 */
export interface AnalyzeMultipleProcedureRunsButtonProps {
  procedureRunIds: Array<string>;
  procedureType: ProcedureType;
  studyId: string;
  studyName: string;
  studyHumanId: string;
  studyType: StudyType;
  isStudyArchived: boolean;
  actionOrigin: ActionOrigin;
}

export default function AnalyzeMultipleProcedureRunsButton(
  props: AnalyzeMultipleProcedureRunsButtonProps
): JSX.Element {
  let onClickHandler: () => void = noop;

  const {
    studyId,
    studyName,
    studyHumanId,
    studyType,
    procedureRunIds,
    procedureType,
    isStudyArchived,
    actionOrigin,
  } = props;

  onClickHandler = async () => {
    await legacyRunModal(CreateAnalysisModal, {
      actionOrigin,
      onCreateAnalysis: (data: CreateAnalysisParams) =>
        createAnalysisForStudy(data, {
          actionOrigin,
          studyType,
          procedureType,
          sourceObjectIds: procedureRunIds,
        }),
      initialStudySearchItems: [{id: studyId, name: studyName, humanId: studyHumanId}],
      disableEditStudies: true,
      title: 'Generate analysis',
      initialAnalysisName: () => getInitialAnalysisNameForStudy(studyName),
      headerInlineBanner: <ProcedureBasedStudyAnalysisModalBanner studyType={studyType} />,
      hideDataSharingWarningBanner: true,
      initialSeedDataConfig: {
        type: SeedDataConfigType.MULTIPLE_PROCEDURE_RUNS,
        data: {
          procedureRunIds,
        },
      },
    });
  };

  let tooltipText: string | null = null;
  const buttonDisabledDueToArchivedStudy = isStudyArchived;
  const buttonDisabledDueToPermissions = !isFeatureEnabledForProcedureType(
    procedureType,
    ProceduresFeature.CREATE_ANALYSIS
  );
  const buttonDisabledDueToNoRuns = procedureRunIds.length === 0;
  const buttonDisabled =
    buttonDisabledDueToArchivedStudy || buttonDisabledDueToPermissions || buttonDisabledDueToNoRuns;
  if (buttonDisabledDueToArchivedStudy) {
    tooltipText = STUDY_ARCHIVED_MESSAGE;
  } else if (buttonDisabledDueToPermissions) {
    tooltipText = CREATE_ANALYSIS_NO_PERMISSION_MESSAGE;
  } else if (buttonDisabledDueToNoRuns) {
    tooltipText = NO_PROCEDURE_RUNS_TO_ANALYZE_MESSAGE;
  }

  return (
    <Tooltip text={tooltipText} isDisabled={!buttonDisabled || tooltipText == null}>
      <Button
        dataTestAttrs={{
          component: 'AnalyzeMultipleProcedureRunsButton',
          element: 'root',
        }}
        buttonStyle={ButtonStyle.TERTIARY_GRAY}
        size={ButtonSize.MEDIUM}
        onClick={onClickHandler}
        text="Analyze"
        icon={ModelTypeConstants.ANALYSIS}
        disabled={buttonDisabled}
      />
    </Tooltip>
  );
}
